import { isNil } from '@lucidlink/tscore';
import { FetcherConfigurationError, UnauthorizedError, } from './fetch-error.types';
import { JSON_HEADERS } from './types';
export const createFetcher = ({ baseUrl: inputUrl, baseUrlHasVersion, fetch, getAccessToken, traceIdGenerator, defaultHeaders, }) => {
    let transformedBaseUrl = new URL(inputUrl);
    normalizeUrl(transformedBaseUrl);
    let version = undefined;
    if (baseUrlHasVersion) {
        const allParts = transformedBaseUrl.toString().split('/');
        version = allParts.pop();
        transformedBaseUrl = new URL(allParts.join('/'));
    }
    return new FetcherImpl(new URL(transformedBaseUrl), version, [], fetch, getAccessToken, traceIdGenerator, defaultHeaders);
};
class FetcherImpl {
    baseUrl;
    version;
    urlSufix;
    fetch;
    getAccessToken;
    traceIdGenerator;
    defaultHeaders;
    constructor(baseUrl, version, urlSufix, fetch, getAccessToken, traceIdGenerator, defaultHeaders) {
        this.baseUrl = baseUrl;
        this.version = version;
        this.urlSufix = urlSufix;
        this.fetch = fetch;
        this.getAccessToken = getAccessToken;
        this.traceIdGenerator = traceIdGenerator;
        this.defaultHeaders = defaultHeaders;
    }
    getBaseUrl() {
        return new URL(this.baseUrl);
    }
    createSubFetcher({ path = [], getAccessToken, stripSufixes, }) {
        return new FetcherImpl(this.baseUrl, this.version, stripSufixes
            ? []
            : [...this.urlSufix, ...(Array.isArray(path) ? path : [path])], this.fetch, getAccessToken ?? this.getAccessToken, this.traceIdGenerator, this.defaultHeaders);
    }
    get(path, params = {}) {
        return this.doFetch('GET', path, params);
    }
    post(path, params = {}) {
        return this.doFetch('POST', path, params);
    }
    put(path, params = {}) {
        return this.doFetch('PUT', path, params);
    }
    patch(path, params = {}) {
        return this.doFetch('PATCH', path, params);
    }
    delete(path, params = {}) {
        return this.doFetch('DELETE', path, params);
    }
    async doFetch(method, path, params) {
        if (params.version && !this.version) {
            throw new FetcherConfigurationError({
                method,
                url: this.baseUrl.toString(),
                details: {
                    message: `
                        Cannot use version parameter with a non-versioned fetcher.
                        Create the fetcher with baseUrlHasVersion: true to enable version support.
                    `,
                    requestedVersion: params.version,
                },
            });
        }
        let result = await this.doRequest(method, path, params);
        if (!result.success &&
            result.error instanceof UnauthorizedError &&
            this.getAccessToken) {
            // Retry the request and force token reissue
            result = await this.doRequest(method, path, params, true);
        }
        if (!result.success) {
            throw result.error;
        }
        if (params.parser) {
            return params.parser(result.body);
        }
        return result.body;
    }
    async doRequest(method, path, { headers: paramsHeaders, body, queryParams, version, traceId: paramTraceId, }, forceTokenRefresh = false) {
        const requestUrl = concatPath(this.baseUrl, this.constructPath(path, version));
        const traceId = paramTraceId ?? this.traceIdGenerator?.();
        const authorizationHeader = await this.getAuthorizationHeader(forceTokenRefresh);
        const headers = {
            ...this.defaultHeaders,
            ...JSON_HEADERS,
            ...paramsHeaders,
            ...authorizationHeader,
        };
        if (traceId) {
            headers['X-Trace-Id'] = traceId;
        }
        return await this.fetch({
            method,
            requestUrl,
            queryParams: trimQueryParams(queryParams),
            headers,
            body: body,
        });
    }
    constructPath(path, versionOverwrite) {
        const normalizedPath = Array.isArray(path) ? path : [path];
        const versionToUse = versionOverwrite ?? this.version;
        if (versionToUse) {
            return [versionToUse, ...this.urlSufix, ...normalizedPath];
        }
        return [...this.urlSufix, ...normalizedPath];
    }
    async getAuthorizationHeader(forceTokenRefresh) {
        if (!this.getAccessToken) {
            return {};
        }
        return {
            Authorization: `Bearer ${await this.getAccessToken(forceTokenRefresh)}`,
        };
    }
}
export const normalizeUrl = (url) => {
    url.pathname = url.pathname.replace(/\/+/g, '/');
    url.pathname = url.pathname.replace(/\/$/, '');
};
export const concatPath = (url, path) => {
    if (path === '' || (Array.isArray(path) && path.length === 0)) {
        return url;
    }
    const resultUrl = new URL(url);
    if (resultUrl.pathname.length !== 1) {
        resultUrl.pathname += '/';
    }
    if (Array.isArray(path)) {
        resultUrl.pathname += path.join('/');
    }
    else {
        resultUrl.pathname += path;
    }
    normalizeUrl(resultUrl);
    return resultUrl;
};
const trimQueryParams = (queryParams) => {
    if (isNil(queryParams)) {
        return undefined;
    }
    const trimmedQueryParams = { ...queryParams };
    Object.keys(trimmedQueryParams).forEach((key) => isNil(trimmedQueryParams[key]) && delete trimmedQueryParams[key]);
    return trimmedQueryParams;
};
